import React, { useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { useAtom } from "jotai";
import { useUpdateMyPresence } from "@liveblocks/react";
import { Button } from "../components/Button";
import { useAuthFlow, AuthStep } from "../utils/useAuthFlow";
import { isInAuthFlowAtom } from "../jotai/atoms";
import { AuthDialog } from "./auth/AuthDialog";
import { CheckPlayerIdMessage } from "./auth/CheckPlayerIdMessage";
import {
  InputNew,
  SubTitle,
  Title,
  ContinueButton,
  DetailText,
  SubTitleSentence,
} from "./auth/AuthDialogComponents";
import { AuthOverlay } from "./auth/AuthOverlay";

export function LogInByLinkButton({
  redirect,
  label,
}: {
  redirect?: string;
  label?: string;
}) {
  return (
    <>
      <Button
        size="large"
        color="primary"
        onClick={() => {
          window.location.replace(
            `/login/${redirect ? `?redirect=${redirect}` : ""}`
          );
        }}
      >
        {label || "Log in"}
      </Button>
    </>
  );
}

export function LogInFromSpaceButton() {
  const [isInAuthFlow, setIsInAuthFlow] = useAtom(isInAuthFlowAtom);
  const updateMyPresence = useUpdateMyPresence();
  const {
    step,
    isSubmitting,
    resetStep,
    email,
    emailInput,
    onEmailChange,
    submitEmail,
    emailCode,
    emailCodeInput,
    emailCodeError,
    onEmailCodeChange,
    submitEmailCode,
    resendEmailCode,
    playerId,
    playerIdInput,
    playerIdCheckResultCode,
    onPlayerIdChange,
    checkPlayerId,
    submitPlayerId,
  } = useAuthFlow({
    /** Notify other components of the app that the player has logged in. */
    onSuccess: () => {
      setIsInAuthFlow(false);
      updateMyPresence({ isInAuthFlow: false });
    },
  });

  useEffect(() => {
    if (isInAuthFlow) {
      updateMyPresence({ isInAuthFlow: true });
    }
  }, [isInAuthFlow, updateMyPresence]);

  /** Disallow scrolling when the player is in the auth flow. */
  useEffect(() => {
    const body = document.body;
    if (!body) return;
    if (isInAuthFlow) {
      body.style.overflow = "hidden";
    } else {
      body.style.overflow = "auto";
    }
  }, [isInAuthFlow]);

  const closeDialog = useCallback(() => {
    resetStep();
    setIsInAuthFlow(false);
    updateMyPresence({ isInAuthFlow: false });
  }, [resetStep, setIsInAuthFlow, updateMyPresence]);

  const overlayOffset = {
    transform: `translate(${window.scrollX}px, ${window.scrollY}px)`,
  };

  if (!isInAuthFlow) return null;

  switch (step) {
    case AuthStep.EnterEmail: {
      return (
        <>
          {ReactDOM.createPortal(
            <AuthOverlay translucent={true} style={overlayOffset}>
              <AuthDialog onClose={closeDialog}>
                <form onSubmit={submitEmail}>
                  <Title style={{ marginBottom: "0.5rem" }}>
                    Welcome to Sprout
                  </Title>
                  <SubTitle style={{ marginBottom: "2rem" }}>
                    We’re so excited that you’re here!
                  </SubTitle>
                  <InputNew
                    value={email}
                    aria-label="Email"
                    type="email"
                    name="email"
                    placeholder="Email address"
                    ref={emailInput}
                    required
                    autoFocus
                    onChange={onEmailChange}
                    disabled={isSubmitting}
                    style={{ marginBottom: "3rem", width: "100%" }}
                  />
                  <ContinueButton
                    color="primary"
                    style={{ marginBottom: "2rem" }}
                    disabled={isSubmitting || email.length === 0}
                  >
                    Continue
                  </ContinueButton>
                  <DetailText style={{ maxWidth: "15rem" }}>
                    By continuing, you’re agreeing to our Terms of Use and
                    Privacy Policy.
                  </DetailText>
                </form>
              </AuthDialog>
            </AuthOverlay>,
            document.body
          )}
        </>
      );
    }
    case AuthStep.EnterEmailCode: {
      return (
        <>
          {ReactDOM.createPortal(
            <AuthOverlay translucent={true} style={overlayOffset}>
              <AuthDialog onClose={closeDialog} closable={false}>
                <form onSubmit={submitEmailCode}>
                  <Title style={{ marginBottom: "0.5rem" }}>
                    Enter login code
                  </Title>
                  <SubTitle style={{ marginBottom: "2rem" }}>
                    Enter the six-digit code sent to {email}.
                  </SubTitle>
                  <InputNew
                    value={emailCode}
                    aria-label="Login code"
                    aria-describedby={
                      emailCodeError ? "email-code-error" : undefined
                    }
                    aria-invalid={Boolean(emailCodeError)}
                    type="text"
                    name="email-code"
                    placeholder="000000"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    ref={emailCodeInput}
                    required
                    autoFocus
                    onChange={onEmailCodeChange}
                    disabled={isSubmitting}
                    style={{
                      marginBottom: emailCodeError ? "0.75rem" : "1.5rem",
                      width: "100%",
                      fontSize: "1.5rem",
                      letterSpacing: "0.25em",
                      textAlign: "center",
                    }}
                  />
                  {emailCodeError && (
                    <DetailText
                      id="email-code-error"
                      role="alert"
                      style={{ color: "#8A2F2A", marginBottom: "1.5rem" }}
                    >
                      {emailCodeError}
                    </DetailText>
                  )}
                  <ContinueButton
                    color="primary"
                    disabled={isSubmitting || emailCode.length !== 6}
                    style={{ marginBottom: "0.75rem", width: "100%" }}
                  >
                    {isSubmitting ? "Verifying…" : "Verify code"}
                  </ContinueButton>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <ContinueButton
                      type="button"
                      color="secondary"
                      onClick={resendEmailCode}
                      disabled={isSubmitting}
                      style={{ flex: 1 }}
                    >
                      Resend code
                    </ContinueButton>
                    <ContinueButton
                      type="button"
                      color="secondary"
                      onClick={resetStep}
                      disabled={isSubmitting}
                      style={{ flex: 1 }}
                    >
                      Change email
                    </ContinueButton>
                  </div>
                </form>
              </AuthDialog>
            </AuthOverlay>,
            document.body
          )}
        </>
      );
    }
    case AuthStep.EnterPlayerId: {
      return (
        <>
          {ReactDOM.createPortal(
            <AuthOverlay translucent={true} style={overlayOffset}>
              <AuthDialog onClose={closeDialog}>
                <form onSubmit={submitPlayerId}>
                  <Title style={{ marginBottom: "0.5rem" }}>
                    Claim your Sprout ID
                  </Title>
                  <SubTitle style={{ marginBottom: "2rem" }}>
                    <SubTitleSentence>
                      Pick a unique Sprout ID that you’d like to use!
                    </SubTitleSentence>
                    <SubTitleSentence>
                      Your Sprout ID will be public. It’s different than
                      nickname, which is next to your cursor that others see in
                      space.
                    </SubTitleSentence>
                  </SubTitle>
                  <InputNew
                    value={playerId}
                    aria-label="Sprout ID"
                    type="text"
                    name="playerId"
                    placeholder="Sprout ID"
                    ref={playerIdInput}
                    required
                    autoFocus
                    onChange={onPlayerIdChange}
                    onBlur={checkPlayerId}
                    disabled={isSubmitting}
                    style={{ marginBottom: "0.5rem", width: "100%" }}
                  />
                  <CheckPlayerIdMessage
                    isChecking={isSubmitting}
                    resultCode={playerIdCheckResultCode}
                  />
                  <ContinueButton
                    color="primary"
                    disabled={isSubmitting || playerId.length === 0}
                    style={{ marginTop: "1.5rem" }}
                  >
                    Continue
                  </ContinueButton>
                </form>
              </AuthDialog>
            </AuthOverlay>,
            document.body
          )}
        </>
      );
    }
    case AuthStep.Done: {
      return (
        <>
          {ReactDOM.createPortal(
            <AuthOverlay translucent={true} style={overlayOffset}>
              <AuthDialog closable={false}>
                <Title style={{ marginBottom: "0.5rem" }}>
                  You’re good to go
                </Title>
                <SubTitle style={{ marginBottom: "2rem" }}>
                  We appreciate that you’re here, have fun! ☀️
                </SubTitle>
                <ContinueButton color="secondary" onClick={closeDialog}>
                  Done
                </ContinueButton>
              </AuthDialog>
            </AuthOverlay>,
            document.body
          )}
        </>
      );
    }
    case AuthStep.Error:
    default: {
      return (
        <>
          {ReactDOM.createPortal(
            <AuthOverlay translucent={true} style={overlayOffset}>
              <AuthDialog onClose={closeDialog}>
                <Title style={{ marginBottom: "0.5rem" }}>
                  Oops, something went wrong...
                </Title>
                <SubTitle style={{ marginBottom: "2rem" }}>
                  We had trouble logging you in. Please try again.
                </SubTitle>
                <ContinueButton color="secondary" onClick={resetStep}>
                  Try again
                </ContinueButton>
              </AuthDialog>
            </AuthOverlay>,
            document.body
          )}
        </>
      );
    }
  }
}
